<template>
	<div class="content-block" :id="id">
		<div v-if="tag" class="content-block__tag">{{ tag }}</div>
		<div class="content-block__header">
			<DefaultIcon
				v-if="logo"
				:logo="logo"
				class="content-block__icon"
			></DefaultIcon>
			<SvgIcon
				v-else-if="icon"
				:icon="icon"
				color="default"
				class="content-block__icon"
			/>
			<h2 class="content-block__title" v-if="title">
				{{ title }}
			</h2>
		</div>
		<InlineVideoPlayer
			v-if="video"
			:thumbnail-poster="images?.[0]"
			:src="video"
			class="content-block__video"
		/>
		<div
			v-else-if="images && (images.image || images.length > 0)"
			class="content-block__images"
		>
			<ZoomImage
				v-if="isZoomImage"
				:fields="{}"
				:image="images.image"
				:image-high-res="images.imageHighRes"
				class="content-block__img"
			/>
			<template v-else-if="images.length > 0">
				<PictureImg
					v-for="image in images"
					:key="image.src"
					:media="image"
					class="content-block__img"
				/>
			</template>
		</div>
		<div class="content-block__content" v-if="content" v-html="content" />
		<DefaultButton
			v-if="(ctaLink && ctaLink.url) || ctaModal"
			:cta-link="ctaLink"
			:icon="ctaIcon"
			:color="ctaColor"
			:gtm="ctaGtm"
			:modal="ctaModal"
			:disabled="ctaDisabled"
			:aria-label="ctaAriaLabel"
			class="content-block__button"
		/>
		<DefaultButton
			v-if="modal"
			:modal="modal"
			:cta-link="{ text: modalButtonLabel }"
			color="ghost-light"
			:gtm="modalButtonGtm"
			class="content-block__modal-button"
		/>
		<div v-if="tooltip" class="content-block__tooltip">
			{{ tooltip }}
		</div>
	</div>
</template>

<script setup>
const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const id = computed(() => props.fields?.id?.value);
const tag = computed(() => props.fields?.tag?.value);
const title = computed(() => props.fields?.title?.value);
const icon = computed(() => props.fields?.icon?.value);
const logo = computed(() =>
	props.fields?.logo?.value?.src ? props.fields?.logo?.value : null
);
const isZoomImage = computed(() => props.fields?.isZoomImage?.value);
const content = computed(() => props.fields?.content?.value);
const tooltip = computed(() => props.fields?.tooltip?.value);
const ctaLink = computed(() => props.fields?.ctaLink?.value);
const ctaIcon = computed(() => props.fields?.ctaIcon?.value);
const ctaColor = computed(() => props.fields?.ctaColor?.value);
const ctaGtm = computed(() => props.fields?.ctaGtm?.value);
const ctaDisabled = computed(() => props.fields?.ctaDisabled?.value);
const ctaAriaLabel = computed(() => props.fields?.ctaAriaLabel?.value);
const ctaModal = computed(() => props.fields?.ctaModal);
const modal = computed(() => props.fields?.modal);
const modalButtonLabel = computed(() => props.fields?.modalButtonLabel?.value);
const modalButtonGtm = computed(() => props.fields?.modalButtonGtm?.value);
const video = computed(() => props.fields?.video?.value?.href);
const images = computed(() => {
	if (isZoomImage.value && !video.value) {
		return {
			image: getImageObj(props.fields?.images?.[0]),
			imageHighRes: getImageObj(props.fields?.images?.[1]),
		};
	} else {
		return props.fields?.images?.slice(0, 4).map((image) => {
			return getImageObj(image);
		});
	}
});

function getImageObj(image) {
	if (!image) return null;
	return {
		src: image.url,
		alt: image.fields?.Alt?.value,
		width: image.fields?.Width?.value,
		height: image.fields?.Height?.value,
	};
}
</script>

<style lang="scss">
.content-block {
	position: relative;
	border-radius: 16px;
	padding: var(--spacing-2, 16px);
	scroll-margin-top: calc(var(--header-height, 54px) + var(--spacing-2, 16px));
	display: grid;
	grid-template-areas:
		"tag"
		"header"
		"images"
		"content"
		"button"
		"modal-button"
		"tooltip";

	.content-block__tag {
		grid-area: tag;
		color: #191a1a;
		background-color: var(--primary-color, #2eac83);
		font-size: var(--text-xs, 12px);
		font-weight: 700;
		line-height: 12px;
		padding: var(--spacing-1, 8px);
		border-radius: var(--spacing-05, 4px);
		width: fit-content;
		margin-bottom: var(--spacing-1, 8px);
	}

	.content-block__header {
		width: 100%;
		grid-area: header;
		display: flex;
		align-items: center;
		margin: var(--spacing-1, 8px) 0 var(--spacing-2, 16px) 0;
	}

	.content-block__images {
		grid-area: images;
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: row;
		flex-wrap: wrap;
		gap: var(--spacing-2, 16px);
		margin-bottom: var(--spacing-1, 8px);
	}

	.content-block__content {
		grid-area: content;
		color: var(--color-grey-3, #989898);
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-base, 16px);
		font-weight: 300;
		line-height: 140%;

		a {
			color: inherit;
			text-decoration: underline;
		}

		p,
		li {
			line-height: 140%;
			margin: 0 0 var(--spacing-2, 16px) 0;
		}

		strong {
			color: var(--color-white, #fff);
		}

		iframe {
			max-width: 100%;
		}

		img {
			max-width: 100%;
		}
	}

	.content-block__button {
		grid-area: button;
		width: 100%;
		margin-top: var(--spacing-2, 16px);
	}

	.content-block__modal-button {
		grid-area: modal-button;
		width: 100%;
		border: none;
		text-decoration: underline;
		font-size: var(--text-sm, 14px);
		font-weight: 500;
		line-height: 140%;
	}

	.content-block__tooltip {
		grid-area: tooltip;
		color: var(--color-grey-3, #989898);
		font-family: var(--primary-font, sans-serif);
		font-size: var(--Typography-Size-xs, 12px);
		text-align: center;
		font-weight: 300;
		line-height: 128%;
		margin-top: var(--spacing-2, 16px);
	}

	.content-block__icon {
		width: 32px;
		height: 32px;
		margin-right: var(--spacing-105, 12px);
		padding: 0;
		display: flex;
		justify-content: center;
		flex-shrink: 0;
	}

	.content-block__title {
		width: 100%;
		margin: 0;
		color: var(--color-white, #fff);
		font-family: var(--primary-font, sans-serif);
		font-size: 28px;
		font-weight: 900;
		line-height: 110%;
		text-transform: uppercase;
	}

	.zoom-img {
		width: 100%;
		max-width: 100%;
		padding: 0;
	}

	.picture-img-wrapper {
		width: 100%;
	}

	.picture-img img {
		padding: 0;
		border-radius: 16px;
		overflow: hidden;
		height: 100%;
	}

	.picture-img-wrapper:not(:only-child) {
		flex-basis: 45%;
	}

	.content-block__content-lounge {
		display: flex;
		flex-wrap: wrap;
	}

	.content-block_svg-link {
		flex-basis: 50%;
		display: flex;
		align-items: center;
		flex-direction: row;
		gap: var(--spacing-1, 8px);
		color: var(--color-white, #fff);
		text-decoration: none;
		margin-bottom: var(--spacing-2, 16px);
	}

	iframe {
		width: 100%;
		height: auto;
		aspect-ratio: 16 / 9;
	}

	/**
		MODIFIER STYLES SECTION ------------------------------
	*/

	&.content-block--tickets {
		margin: var(--spacing-2, 16px);
		grid-template-areas:
			"tag"
			"images"
			"header"
			"content"
			"button"
			"modal-button"
			"tooltip";

		&:before {
			content: "";
			background: linear-gradient(0deg, #fff0 20%, #989898);
			position: absolute;
			padding: 1px;
			inset: -1px;
			border-radius: inherit;
			-webkit-mask: linear-gradient(#000, #000) content-box,
				linear-gradient(#000, #000);
			mask: linear-gradient(#000, #000) content-box, linear-gradient(#000, #000);
			-webkit-mask-composite: xor;
			mask-composite: exclude;
		}

		.content-block__header {
			padding-bottom: var(--spacing-1, 8px);
			border-bottom: 1px solid var(--color-grey-3, #989898);

			.content-block__title {
				color: var(--color-white, #fff);
				font-family: var(--primary-font, sans-serif);
				font-size: var(--text-lg, 20px);
				font-weight: 700;
				line-height: 120%;
			}
		}

		.content-block__content {
			p,
			ul {
				font-size: var(--text-sm, 14px);
			}

			li::marker {
				color: var(--color-white, #fff);
			}
		}
	}

	&.content-block--welcome {
		text-align: center;
		margin-bottom: var(--spacing-8, 64px);
		margin-top: var(--spacing-8, 64px);

		grid-template-areas:
			"images"
			"tag"
			"header"
			"content"
			"button"
			"modal-button"
			"tooltip";

		.content-block__img {
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 0;

			img {
				width: 220px;
				max-width: 80%;
				margin: 0 24px 24px 24px;
			}
		}

		.content-block__title {
			text-align: center;
			font-size: var(--text-4xl, 48px);
			font-weight: 400;
			line-height: 120%;
		}

		.content-block__content p {
			color: #f4f2ff;
			font-size: var(--text-lg, 20px);
			font-weight: 400;
			line-height: 120%;
		}
	}

	&.content-block--grey {
		background: var(--Colors-Neutrals-Grey-7, #282828);
		grid-template-areas:
			"tag"
			"header"
			"images"
			"content"
			"button"
			"modal-button"
			"tooltip";

		.content-block__tooltip {
			text-align: left;
		}
	}

	&.content-block--inverse {
		grid-template-areas:
			"tag"
			"header"
			"content"
			"images"
			"button"
			"modal-button"
			"tooltip";
	}

	// Did you know section style
	&.content-block--list {
		grid-template-areas:
			"tag"
			"header"
			"content"
			"images"
			"button"
			"modal-button"
			"tooltip";

		.content-block__title {
			font-size: var(--text-2xl, 32px);
		}

		ul {
			list-style-type: none;
			padding: 0;
			margin-bottom: var(--spacing-4, 32px);
		}

		li {
			padding: var(--spacing-2, 16px) 0 0 0;
			margin: var(--spacing-2, 16px) 0;
			border-top: 1px solid #454545;
			color: white;
		}
	}

	&.content-block--small {
		.content-block__title {
			font-size: var(--text-sm, 14px);
		}

		.content-block__content {
			p,
			ul {
				font-size: var(--text-xs, 12px);
			}
		}
	}

	&.content-block--card-primary {
		grid-template-areas:
			"tag"
			"header"
			"content"
			"images"
			"button"
			"modal-button"
			"tooltip";
		border-radius: 20px;
		background-color: var(--primary-color);

		.content-block__tag {
			background-color: var(--secondary-color);
			color: #191a1a;
		}

		.content-block__title {
			color: #191a1a;
		}

		.content-block__content {
			color: #191a1a;
		}

		.content-block__tooltip {
			color: #191a1a;
			text-align: left;
		}
	}

	&.content-block--image-first {
		grid-template-areas:
			"tag"
			"images"
			"header"
			"content"
			"button"
			"modal-button"
			"tooltip";
	}
}
</style>
